/*
 * Selected-filters chips (voorheen inline in blocks/filters.liquid).
 *
 * PERF-fixes t.o.v. de inline versie:
 *  1. De MutationObserver observeerde document.body met subtree:true en riep
 *     renderFilterChips() aan. Die functie muteert zelf de DOM binnen body, waardoor
 *     de observer opnieuw afvuurde -> een oneindige render-loop van ~8x/sec, elk met
 *     getBoundingClientRect() + getComputedStyle() per chip. Nu worden mutaties binnen
 *     onze eigen toolbar genegeerd.
 *  2. De click/change-handlers deden 7x getElementById bij ELKE klik op de pagina.
 *     Nu is er een goedkope early-exit voordat er iets wordt opgezocht.
 *  3. Breedtes worden per render eenmalig uitgelezen i.p.v. per item opnieuw.
 *  4. Geen Liquid meer nodig -> bestand is cachebaar en staat niet in elke HTML-response.
 *
 * V19 FIX -- de observer overleefde de sectie-render niet. Hij stond op elements.form,
 * en assets/facets.js vervangt bij elke filterklik de hele sectie-DOM. Daarna
 * observeerde hij een losgekoppelde weesnode en kwam er geen update meer; het enige
 * vangnet was de vaste setTimeout(..., 450) hieronder, die bij een trage render of een
 * grote collectie te vroeg afgaat en verouderde chips laat staan. De observer staat nu
 * op de omliggende .shopify-section, die de render wel overleeft, en herbindt zichzelf
 * zodra het formulier is vervangen. De 450 ms blijft als extra vangnet staan.
 *
 * V19 -- de twee zichtbare teksten ('CLEAR ALL' en het aria-label 'Remove filter')
 * stonden hardgecodeerd in het Engels en waren dus onvertaalbaar. Ze komen nu uit
 * data-attributen op de root, met exact de oude Engelse tekst als terugval. Zolang
 * blocks/filters.liquid die attributen niet meegeeft verandert er niets; zodra dat wel
 * gebeurt (met | t) zijn ze vertaald.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 750;
  var observers = new WeakMap();
  var observedScopes = new WeakMap();

  function debounce(fn, delay) {
    var timeout;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  function getRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-selected-filters-root]'));
  }

  /* Leest een label uit een data-attribuut op de root, met de oude Engelse tekst
     als terugval zodat er zonder Liquid-wijziging niets verandert. */
  function labelFrom(root, key, fallback) {
    if (!root || !root.dataset) return fallback;
    var value = root.dataset[key];
    if (typeof value !== 'string') return fallback;
    value = value.trim();
    return value ? value : fallback;
  }

  function getElements(root) {
    if (!root) return null;

    return {
      root: root,
      form: document.getElementById(root.dataset.formId),
      container: document.getElementById(root.dataset.containerId),
      firstRow: document.getElementById(root.dataset.firstRowId),
      overflowRow: document.getElementById(root.dataset.overflowId),
      measureRow: document.getElementById(root.dataset.measureId)
    };
  }

  function isComplete(elements) {
    return !!(
      elements &&
      elements.form &&
      elements.container &&
      elements.firstRow &&
      elements.overflowRow &&
      elements.measureRow
    );
  }

  function getCheckedInputs(form) {
    if (!form) return [];
    return Array.prototype.slice.call(form.querySelectorAll('input[type="checkbox"]:checked'));
  }

  function getLabelText(input) {
    if (!input) return '';

    var nextText =
      input.nextElementSibling && input.nextElementSibling.textContent
        ? input.nextElementSibling.textContent.trim()
        : '';

    if (nextText) return nextText;

    var label = input.closest('label');
    if (label && label.textContent) return label.textContent.trim();

    return input.value || '';
  }

  function createChipItem(root, name, value, labelText) {
    var li = document.createElement('li');
    li.className = 'selected-filters__chip';

    var span = document.createElement('span');
    span.className = 'selected-filters__chip-label';
    span.textContent = labelText;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'selected-filters__chip-remove';
    button.dataset.name = name;
    button.dataset.val = value;
    button.setAttribute('aria-label', labelFrom(root, 'labelRemoveFilter', 'Remove filter'));
    button.textContent = '×';

    li.appendChild(span);
    li.appendChild(button);
    return li;
  }

  function createClearAllItem(root) {
    var li = document.createElement('li');
    li.className = 'selected-filters__clear-all-item';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'selected-filters__clear-all-button';
    button.textContent = labelFrom(root, 'labelClearAll', 'CLEAR ALL');

    li.appendChild(button);
    return li;
  }

  function measureItems(measureRow) {
    // Alle metingen in een keer, voordat er iets wordt geschreven: voorkomt layout thrashing.
    return Array.prototype.map.call(measureRow.children, function (item) {
      var style = window.getComputedStyle(item);
      var marginRight = parseFloat(style.marginRight) || 0;
      return {
        node: item,
        width: Math.ceil(item.getBoundingClientRect().width + marginRight)
      };
    });
  }

  function distributeItems(elements) {
    var measured = measureItems(elements.measureRow);
    var firstFragment = document.createDocumentFragment();
    var overflowFragment = document.createDocumentFragment();

    if (window.innerWidth < MOBILE_BREAKPOINT) {
      measured.forEach(function (entry, index) {
        (index === 0 ? firstFragment : overflowFragment).appendChild(entry.node.cloneNode(true));
      });
    } else {
      var availableWidth = Math.floor(elements.firstRow.getBoundingClientRect().width);

      if (availableWidth <= 0) {
        measured.forEach(function (entry) {
          overflowFragment.appendChild(entry.node.cloneNode(true));
        });
      } else {
        var usedWidth = 0;
        var firstRowHasItems = false;

        measured.forEach(function (entry) {
          if (usedWidth + entry.width <= availableWidth) {
            firstFragment.appendChild(entry.node.cloneNode(true));
            usedWidth += entry.width;
            firstRowHasItems = true;
          } else {
            overflowFragment.appendChild(entry.node.cloneNode(true));
          }
        });

        if (!firstRowHasItems && measured.length) {
          firstFragment.textContent = '';
          overflowFragment.textContent = '';
          measured.forEach(function (entry, index) {
            (index === 0 ? firstFragment : overflowFragment).appendChild(entry.node.cloneNode(true));
          });
        }
      }
    }

    elements.firstRow.replaceChildren(firstFragment);
    elements.overflowRow.replaceChildren(overflowFragment);
  }

  function renderFilterChips(elements) {
    if (!isComplete(elements)) return;

    elements.measureRow.replaceChildren();

    var checkedInputs = getCheckedInputs(elements.form);
    var seenFilters = new Set();
    var fragment = document.createDocumentFragment();

    checkedInputs.forEach(function (input) {
      var filterKey = input.name + ':' + input.value;
      if (seenFilters.has(filterKey)) return;
      seenFilters.add(filterKey);
      fragment.appendChild(createChipItem(elements.root, input.name, input.value, getLabelText(input)));
    });

    if (!seenFilters.size) {
      elements.firstRow.replaceChildren();
      elements.overflowRow.replaceChildren();
      elements.container.hidden = true;
      return;
    }

    fragment.appendChild(createClearAllItem(elements.root));
    elements.measureRow.replaceChildren(fragment);
    elements.container.hidden = false;
    distributeItems(elements);
  }

  function renderAll() {
    getRoots().forEach(function (root) {
      renderFilterChips(getElements(root));
    });
  }

  var debouncedRenderAll = debounce(renderAll, 120);

  /* De sectie overleeft de render van facets.js; het formulier niet. */
  function scopeFor(elements) {
    return (
      (elements.form && elements.form.closest('.shopify-section')) ||
      (elements.form && elements.form.parentElement) ||
      elements.form
    );
  }

  function observe(elements) {
    if (!isComplete(elements)) return;

    var scope = scopeFor(elements);
    if (!scope) return;

    // Al een observer op deze root EN nog op dezelfde scope: niets te doen.
    if (observers.has(elements.root) && observedScopes.get(elements.root) === scope) return;

    var existing = observers.get(elements.root);
    if (existing) existing.disconnect();

    var schedule = debounce(function () {
      var fresh = getElements(elements.root);
      renderFilterChips(fresh);
      // Is het formulier vervangen, dan opnieuw binden op de nieuwe scope.
      if (isComplete(fresh)) observe(fresh);
    }, 120);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        // Mutaties binnen onze eigen toolbar zijn onze eigen writes -> negeren.
        // Dit is wat de oneindige render-loop uit de oude versie doorbreekt.
        if (!elements.root.contains(mutations[i].target)) {
          schedule();
          return;
        }
      }
    });

    observer.observe(scope, { childList: true, subtree: true });
    observers.set(elements.root, observer);
    observedScopes.set(elements.root, scope);
  }

  function findElementsForNode(node) {
    var roots = getRoots();
    for (var i = 0; i < roots.length; i++) {
      var elements = getElements(roots[i]);
      if (isComplete(elements) && elements.form.contains(node)) return elements;
    }
    return null;
  }

  function findInputByNameAndValue(form, name, value) {
    if (!form) return null;
    var inputs = form.querySelectorAll('input[type="checkbox"]');

    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === name && inputs[i].value === value) return inputs[i];
    }

    return null;
  }

  function getCustomClearAllUrl() {
    var currentUrl = new URL(window.location.href);
    var nextUrl = new URL(currentUrl.origin + currentUrl.pathname);
    var preservedEntries = [];
    var lastSortBy = null;

    currentUrl.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) return;
      if (key === 'style') return;
      if (key === 'context') return;
      if (key === 'page') return;

      if (key === 'sort_by') {
        lastSortBy = value;
        return;
      }

      preservedEntries.push([key, value]);
    });

    preservedEntries.forEach(function (entry) {
      nextUrl.searchParams.append(entry[0], entry[1]);
    });

    if (lastSortBy) nextUrl.searchParams.set('sort_by', lastSortBy);

    return nextUrl.toString();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // Goedkope early-exit: geen DOM-lookups voor klikken elders op de pagina.
    var removeBtn = target.closest('.selected-filters__chip-remove');
    var clearAllBtn = removeBtn ? null : target.closest('.selected-filters__clear-all-button');
    if (!removeBtn && !clearAllBtn) return;

    if (clearAllBtn) {
      event.preventDefault();
      window.location.assign(getCustomClearAllUrl());
      return;
    }

    var elements = findElementsForNode(removeBtn) || getElements(getRoots()[0]);
    if (!isComplete(elements)) return;

    var checkbox = findInputByNameAndValue(elements.form, removeBtn.dataset.name, removeBtn.dataset.val);
    if (!checkbox) return;

    checkbox.checked = false;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(function () {
      renderFilterChips(getElements(elements.root));
    }, 450);
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;

    var elements = findElementsForNode(target);
    if (!elements) return;

    setTimeout(function () {
      renderFilterChips(getElements(elements.root));
    }, 450);
  });

  window.addEventListener('resize', debouncedRenderAll, { passive: true });

  function init() {
    getRoots().forEach(function (root) {
      var elements = getElements(root);
      if (!isComplete(elements)) return;
      observe(elements);
      renderFilterChips(elements);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Facets renderen asynchroon; deze twee vangnetten vervangen de vier losse
  // setTimeout-renders uit de oude versie.
  setTimeout(init, 400);
  setTimeout(init, 1200);

  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 50);
  });

  document.addEventListener('shopify:section:reorder', function () {
    setTimeout(init, 50);
  });
})();
